import {
  toWSDateString,
  useModalOldContext,
  useWSSnackbar,
  WSButton,
  WSButtons,
  WSFormOld,
  WSMenuItem,
  WSModalOld,
  WSText,
  WSInputTextarea
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import {
  FrequencyAndScheduleStatus,
  IInvoiceTemplate
} from "@wingspanhq/payments/dist/interfaces";
import { Invoice, InvoiceStatus } from "@wingspanhq/wingspan-api-sdk";
import React, { useCallback } from "react";
import { useHistory } from "react-router";
import { useLocation } from "react-router-dom";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import { queriesInvoices, queriesInvoiceRows } from "@query";
import { useSession } from "@hooks/useSession";
import { useFeatureFlags } from "../../../query/hooks/useFeatureFlags";
import {
  useMutationAcceptInvoice,
  useMutationCancelInvoiceTemplate,
  useMutationDeleteInvoiceTemplate,
  useMutationDownloadInvoicePdf,
  useMutationDownloadInvoiceReceiptPdf,
  useMutationSendInvoice
} from "../../../query/payments/mutations";
import { useModalMarkInvoiceDeposited } from "../ModalMarkInvoiceDeposited";
import { QUERY_INVOICE, QUERY_INVOICES } from "../../../query/payments/keys";
import { useSetWSStore } from "../../../store";
import {
  calculateInvoiceTempalteDueDate,
  calculateLineItemsTotal
} from "../../utils";

const DELETE_INVOICE_MODAL = "DELETE_INVOICE_MODAL";
const DISPUTE_INVOICE_MODAL = "DISPUTE_INVOICE_MODAL";

export const useGetInvoiceActions = () => {
  const { accountId } = useSession();
  const history = useHistory();
  const location = useLocation();
  const { openModal } = useModalOldContext();
  const { openSnackbar } = useWSSnackbar();
  const [sendInvoice] = useMutationSendInvoice();
  const [sendInvoiceReminder] = queriesInvoices.remindInvoice.useMutation({
    onSuccess: () => {
      openSnackbar({ type: "success", message: "Reminder sent" });
    }
  });
  const [downloadInvoicePdf] = useMutationDownloadInvoicePdf();
  const [downloadInvoiceReceiptPdf] = useMutationDownloadInvoiceReceiptPdf();
  const [acceptInvoice] = useMutationAcceptInvoice();
  const modalMarkDeposited = useModalMarkInvoiceDeposited();
  const featureFlagsQuery = useFeatureFlags();
  const setStore = useSetWSStore();

  return useCallback(
    (invoice: Invoice) => {
      const menuItems: WSMenuItem[] = [];
      const invoiceId = invoice.id!;
      const status = invoice.status;

      const isCreator = !!accountId && invoice.accountId === accountId;
      const hasEditAccess = isCreator;
      const isDisputed =
        !!invoice.events?.memberDisputedAt &&
        (!invoice.events?.clientResolvedDisputeAt ||
          invoice.events.memberDisputedAt >
            invoice.events.clientResolvedDisputeAt);

      // Edit
      if (
        status &&
        [
          InvoiceStatus.CREATED,
          InvoiceStatus.PENDING,
          InvoiceStatus.OPENED,
          InvoiceStatus.OVERDUE
        ].includes(status) &&
        (hasEditAccess || featureFlagsQuery.data?.editAllInvoices)
      ) {
        menuItems.push({
          icon: "edit",
          label: "Edit",
          onClick: () => {
            setStore({ invoiceFormBackPath: location.pathname });
            history.push(`/member/invoices/${invoiceId}/edit`);
          }
        });
      }

      // Duplicate
      menuItems.push({
        icon: "plus-circle",
        label: "Duplicate",
        onClick: () => {
          history.push(`/member/invoices/${invoiceId}/duplicate`);
        }
      });

      // Delete
      if (status === InvoiceStatus.CREATED && hasEditAccess) {
        menuItems.push({
          icon: "exit",
          label: "Remove",
          onClick: () => {
            openModal(DELETE_INVOICE_MODAL, { invoice });
          }
        });
      }

      // Cancel
      if (
        status &&
        [
          InvoiceStatus.OPENED,
          InvoiceStatus.OVERDUE,
          InvoiceStatus.PENDING
        ].includes(status) &&
        hasEditAccess
      ) {
        menuItems.push({
          icon: "exit",
          label: "Cancel invoice",
          onClick: () => {
            openModal(DELETE_INVOICE_MODAL, { invoice });
          }
        });
      }

      // Send
      if (status === InvoiceStatus.CREATED && hasEditAccess) {
        menuItems.push({
          icon: "send",
          label: "Send now",
          onClick: () => {
            sendInvoice({ invoiceId });
          }
        });
      }

      // Mark deposited
      if (
        status &&
        [
          InvoiceStatus.OPENED,
          InvoiceStatus.OVERDUE,
          InvoiceStatus.PAYMENT_IN_TRANSIT
        ].includes(status) &&
        hasEditAccess
      ) {
        menuItems.push({
          icon: "dollar-circle",
          label: "Mark deposited",
          onClick: () => {
            modalMarkDeposited.open({
              invoiceId,
              amount: invoice.amount ?? 0,
              currency: invoice.currency ?? "USD",
              invoiceNumber: invoice.invoiceNumber
            });
          }
        });
      }

      // Download PDF
      if (
        status !== InvoiceStatus.CREATED &&
        status !== InvoiceStatus.PENDING
      ) {
        menuItems.push({
          icon: "download",
          label: "Download PDF",
          onClick: () => {
            downloadInvoicePdf({ invoiceId });
          }
        });
      }

      // Download Receipt PDF
      if (status === InvoiceStatus.PAID) {
        menuItems.push({
          icon: "download",
          label: "Download receipt PDF",
          onClick: () => {
            downloadInvoiceReceiptPdf({ invoiceId });
          }
        });
      }

      // Send reminder
      if (
        status &&
        [InvoiceStatus.OPENED, InvoiceStatus.OVERDUE].includes(status)
      ) {
        menuItems.push({
          icon: "calendar",
          label: "Send reminder",
          onClick: () => {
            sendInvoiceReminder({ invoiceId });
          }
        });
      }

      // Accept
      if (
        status &&
        [
          InvoiceStatus.OPENED,
          InvoiceStatus.OVERDUE,
          InvoiceStatus.PENDING
        ].includes(status) &&
        !isCreator &&
        !invoice.events?.memberAcceptedAt
      ) {
        menuItems.push({
          icon: "check",
          label: "Accept invoice",
          onClick: () => {
            acceptInvoice({ invoiceId });
          }
        });
      }

      // Dispute
      if (
        status &&
        [
          InvoiceStatus.OPENED,
          InvoiceStatus.OVERDUE,
          InvoiceStatus.PENDING
        ].includes(status) &&
        !isCreator &&
        !isDisputed
      ) {
        menuItems.push({
          icon: "alert-circle",
          label: "Dispute invoice",
          onClick: () => {
            openModal(DISPUTE_INVOICE_MODAL, { invoice });
          }
        });
      }

      return menuItems;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.pathname]
  );
};

export const useGetInvoiceTemplateActions = () => {
  const history = useHistory();
  const location = useLocation();
  const { openModal } = useModalOldContext();
  const setStore = useSetWSStore();

  return useCallback(
    (invoiceTemplate: IInvoiceTemplate) => {
      const menuItems: WSMenuItem[] = [];

      if (
        [
          FrequencyAndScheduleStatus.Draft,
          FrequencyAndScheduleStatus.Active
        ].includes(invoiceTemplate.status)
      ) {
        menuItems.push({
          icon: "edit",
          label: invoiceTemplate.frequency ? "Edit series" : "Edit invoice",
          onClick: () => {
            setStore({ invoiceFormBackPath: location.pathname });
            history.push(
              `/member/invoices/template/${invoiceTemplate.invoiceTemplateId}/edit`
            );
          }
        });
      }

      if (
        [
          FrequencyAndScheduleStatus.Draft,
          FrequencyAndScheduleStatus.Active
        ].includes(invoiceTemplate.status)
      ) {
        menuItems.push({
          icon: "plus-circle",
          label: "Duplicate",
          onClick: () => {
            history.push(
              `/member/invoices/template/${invoiceTemplate.invoiceTemplateId}/duplicate`
            );
          }
        });
      }

      if ([FrequencyAndScheduleStatus.Draft].includes(invoiceTemplate.status)) {
        menuItems.push({
          icon: "exit",
          label: "Remove",
          onClick: () => {
            openModal(DELETE_INVOICE_MODAL, {
              invoiceTemplate
            });
          }
        });
      }

      if (
        [FrequencyAndScheduleStatus.Active].includes(invoiceTemplate.status)
      ) {
        menuItems.push({
          icon: "exit",
          label: invoiceTemplate.frequency ? "Cancel series" : "Cancel invoice",
          onClick: () => {
            openModal(DELETE_INVOICE_MODAL, {
              invoiceTemplate
            });
          }
        });
      }

      return menuItems;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.pathname]
  );
};

const INVOICE_LIST_DEPENDENCIES = [
  QUERY_INVOICES,
  queriesInvoiceRows.searchInvoiceRows.queryKeyBase
];

export const InvoiceActions: React.FC = () => {
  const { closeModal } = useModalOldContext();
  const { openSnackbar } = useWSSnackbar();
  const history = useHistory();
  const [deleteInvoice, deleteInvoiceMeta] =
    queriesInvoices.deleteInvoice.useMutation({
      dependencies: INVOICE_LIST_DEPENDENCIES
    });
  const [cancelInvoice, cancelInvoiceMeta] =
    queriesInvoices.voidInvoice.useMutation({
      dependencies: INVOICE_LIST_DEPENDENCIES
    });
  const [deleteInvoiceTemplate, deleteInvoiceTemplateMeta] =
    useMutationDeleteInvoiceTemplate();
  const [cancelInvoiceTemplate, cancelInvoiceTemplateMeta] =
    useMutationCancelInvoiceTemplate();
  const [disputeInvoice, disputeInvoiceMeta] =
    queriesInvoices.disputeInvoice.useMutation({
      dependencies: [...INVOICE_LIST_DEPENDENCIES, QUERY_INVOICE],
      onSuccess: () => {
        openSnackbar({ type: "success", message: "Invoice disputed" });
      }
    });

  return (
    <>
      <WSModalOld name={DELETE_INVOICE_MODAL}>
        {({
          invoice,
          invoiceTemplate,
          listName
        }: {
          invoice?: Invoice;
          invoiceTemplate?: IInvoiceTemplate;
          listName?: "paid" | "list";
        }) => {
          const isCancel = invoice
            ? !!invoice.status &&
              [InvoiceStatus.OPENED, InvoiceStatus.OVERDUE].includes(
                invoice.status
              )
            : invoiceTemplate
              ? invoiceTemplate.status === FrequencyAndScheduleStatus.Active
              : false;

          const isSeries = invoiceTemplate
            ? !!invoiceTemplate.frequency
            : false;

          const toText = "";

          const onSuccess = () => {
            closeModal(DELETE_INVOICE_MODAL);
            history.push(`/member/invoices${listName ? `/${listName}` : ""}`);
          };

          const dueDate = invoice
            ? invoice.dueDate
            : invoiceTemplate
              ? calculateInvoiceTempalteDueDate(invoiceTemplate)
              : new Date();

          const amount = invoice
            ? (invoice.amount ?? 0)
            : invoiceTemplate
              ? calculateLineItemsTotal(invoiceTemplate.invoiceData.lineItems)
              : 0;

          return (
            <>
              <WSText.Heading5 mb="M">
                {isCancel ? "Cancel" : "Delete"}{" "}
                {isSeries ? "series" : "invoice"}
                {toText}
              </WSText.Heading5>
              <WSText mb="M">
                Are you sure you want to {isCancel ? "cancel" : "delete"} your{" "}
                {toWSDateString(dueDate, "monthDayYear")}{" "}
                {isSeries ? "invoices series" : "invoice"}
                {toText} for{" "}
                {formatMoney(
                  amount,
                  invoice?.currency ||
                    invoiceTemplate?.invoiceData.currency ||
                    "USD"
                )}
                ?
              </WSText>
              <WSText mb="XL">
                This action can't be undone, once you{" "}
                {isCancel ? "cancel" : "delete"}{" "}
                {isSeries ? "a series" : "an invoice"} you'll have to recreate
                it.
              </WSText>

              <WSButtons format="modal">
                <WSButton
                  destructive
                  name="delete"
                  onClick={() => {
                    if (invoice) {
                      if (isCancel) {
                        cancelInvoice(
                          { invoiceId: invoice.id! },
                          { onSuccess }
                        );
                      } else {
                        deleteInvoice(
                          { invoiceId: invoice.id! },
                          { onSuccess }
                        );
                      }
                    }
                    if (invoiceTemplate) {
                      if (isCancel) {
                        cancelInvoiceTemplate(
                          {
                            invoiceTemplateId: invoiceTemplate.invoiceTemplateId
                          },
                          { onSuccess }
                        );
                      } else {
                        deleteInvoiceTemplate(
                          {
                            invoiceTemplateId: invoiceTemplate.invoiceTemplateId
                          },
                          { onSuccess }
                        );
                      }
                    }
                  }}
                  loading={
                    cancelInvoiceMeta.isLoading ||
                    deleteInvoiceMeta.isLoading ||
                    cancelInvoiceTemplateMeta.isLoading ||
                    deleteInvoiceTemplateMeta.isLoading
                  }
                >
                  {isCancel ? "Cancel" : "Delete"}{" "}
                  {isSeries ? "series" : "invoice"}
                </WSButton>
                <WSButton.Secondary
                  onClick={() => {
                    closeModal(DELETE_INVOICE_MODAL);
                  }}
                >
                  Keep {isSeries ? "series" : "invoice"}
                </WSButton.Secondary>
              </WSButtons>
            </>
          );
        }}
      </WSModalOld>

      <WSModalOld
        name={DISPUTE_INVOICE_MODAL}
        size="S"
        onClose={() => {
          disputeInvoiceMeta.reset();
        }}
        title="Dispute Invoice"
      >
        {({ invoice }: { invoice: Invoice }) => {
          return (
            <>
              <WSFormOld<{ disputeComment: string }>
                onSubmit={({ disputeComment }) => {
                  disputeInvoice(
                    {
                      invoiceId: invoice.id!,
                      disputeInvoiceRequest: { reason: disputeComment }
                    },
                    {
                      onSuccess: () => {
                        closeModal(DISPUTE_INVOICE_MODAL);
                      }
                    }
                  );
                }}
              >
                <WSText mb="XL">
                  Indicate the reason for the dispute in the field below
                </WSText>

                <WSFormOld.Field
                  mb="XL"
                  name="disputeComment"
                  component={WSInputTextarea}
                />

                <WSErrorMessage
                  mb="XL"
                  error={disputeInvoiceMeta.error}
                  contextKey="DisputeInvoice"
                />

                <WSButtons format="modal">
                  <WSButton
                    name="disputeInvoice"
                    loading={disputeInvoiceMeta.isLoading}
                  >
                    Dispute invoice
                  </WSButton>
                  <WSButton.Secondary
                    name="disputeInvoice"
                    onClick={() => {
                      closeModal(DISPUTE_INVOICE_MODAL);
                    }}
                    type="button"
                  >
                    Cancel
                  </WSButton.Secondary>
                </WSButtons>
              </WSFormOld>
            </>
          );
        }}
      </WSModalOld>
    </>
  );
};
